import { Component, Input, Inject, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { cloneDeep, isEqual, isArray } from 'lodash';
import { MediaService } from '@core/services/media.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'sfx-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss']
})
export class FiltersComponent implements OnInit, OnChanges {
    @Output() toggleRequested = new EventEmitter<void>();

    @Input() filters: any;
    @Input() fixedFilters: boolean;
    @Input() pipeline: any;
    @Input() allPipelines: any;
    @Input() hideToolbar: boolean;
    @Input() hideSavedFilters: boolean;
    @Input() entity: string;
    @Input() group: string;
    @Input() filterHeaderText: string;
    @Input() filterFields: any[];
    @Input() customErrorMessage: string;

    @Input() onFilterApply!: (args: any) => void;
    @Input() onClose: () => void;
    @Input() getDefaultFilters!: () => any[];
    @Input() setErrorMessage!: (args: any) => void;

    @Input() trigger!: Subject<{ action: string, payload?: any }>;
    private triggerSub!: Subscription;

    // Component properties
    filterGroups: any[] = [];
    index = 0;
    showBadFilterInfo = false;
    faultyFilterRules: any = {};
    savedFilters: any[] = [];
    appliedSavedFilter: any = null;
    savedFilterMdSelectModel: any = 0;
    isSavedFilterDirty = false;
    entityPlural: string;
    filterFieldsTranslations: { [key: string]: string } = {};

    constructor(
        private sidenav: MatSidenav,
        public media: MediaService,
        @Inject('dialogService') private dialogService: any,
        @Inject('filterService') public filterService: any,
        @Inject('savedFilterService') private savedFilterService: any,
        @Inject('savedFiltersService') private savedFiltersService: any,
        @Inject('utilsService') public utilsService: any,
        @Inject('flagsService') private flagsService: any,
        @Inject('sfDiscardDialog') private discardDialog: any,
        private translate: TranslateService
    ) {}

    ngOnInit() {
        this.initializeComponent();

        this.triggerSub = this.trigger.subscribe((event: any) => {
            if (event.action === 'setFilters') {
                this.setFiltersHandler(event.payload);
            }
        });

        this.filterFieldsTranslations = {
            'Number of employees': this.translate.instant('Number of employees'),
            'Location': this.translate.instant('Location'),
            'Industry': this.translate.instant('Industry'),
            'Job title': this.translate.instant('Job title'),
            'Founded': this.translate.instant('Founded')
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            (changes.pipeline && changes.pipeline.previousValue && !isEqual({}, changes.pipeline.previousValue)) ||
            (changes.allPipelines && changes.allPipelines.previousValue && !isEqual({}, changes.allPipelines.previousValue)) ||
            (changes.entity && changes.entity.previousValue && !isEqual({}, changes.entity.previousValue))
        ) {
            this.reloadFilterFields();
        }
    }

    private initializeComponent() {
        switch (this.entity) {
            case 'lead':
                this.entityPlural = 'people';
                break;
            case 'opportunity':
                this.entityPlural = 'opportunities';
                break;
            default:
                this.entityPlural = `${this.entity}s`;
        }

        // Translation map for entity names (singular and plural)
        const entityTranslationMap: { [key: string]: string } = {
            task: this.translate.instant('tasks'),
            lead: this.translate.instant('people'),
            opportunity: this.translate.instant('opportunities'),
            contact: this.translate.instant('contacts'),
            account: this.translate.instant('accounts'),
            workflow: this.translate.instant('workflows'),
            tag: this.translate.instant('tags')
        };

        const translatedEntityPlural = entityTranslationMap[this.entity];
        const matchAllText = this.translate.instant('{{entityPlural}} match all of these filters', { entityPlural: translatedEntityPlural });
        this.filterHeaderText = this.filterHeaderText || `${this.capitalizeFirstLetter(matchAllText)}`;

        this.faultyFilterRules.firstLine = this.translate.instant('It looks like you are using filters that include custom fields, custom field values or entities that have been deleted.');
        this.faultyFilterRules.secondLine = this.translate.instant('Please update the filters to continue.');

        if (this.savedFiltersEnabled()) {
            this.getSavedFilters();
        }

        if (this.filterFields) {
            // Translate filterFields if they were provided directly
            this.filterFields = this.filterFields.map((field) => {
                return {
                    ...field,
                    label: this.filterFieldsTranslations[field.label] || field.label
                };
            });
            this.setupInitialFilterGroups();
        }
        else {
            const options = {
                allPipelines: this.allPipelines,
                pipelineId: this.pipeline
            };

            this.filterService.loadFilterFields(this.entity, options).then((response: any) => {
                if (response) {
                    this.filterFields = response.map((field: any) => {
                        return {
                            ...field,
                            label: this.filterFieldsTranslations[field.label] || field.label
                        };
                    });
                    this.setupInitialFilterGroups();
                }
            });
        }
    }

    private setupInitialFilterGroups() {
        const firstFilterGroup = {
            index: this.index,
            filterRules: this.filters || []
        };

        this.filterGroups.push(firstFilterGroup);

        if (this.fixedFilters) {
            this.filters = cloneDeep(this.mergeWithFilterFields(this.filters));
        }

        this.filterGroups[0].filterRules = this.filters;

        if (this.savedFiltersEnabled()) {
            this.getSavedFilters();
        }

        this.checkForErrors(this.filters);
    }

    private setFiltersHandler(payload: any) {

        const newRules = this.filterService.flatten(payload);
        const oldRules = this.filterService.flatten(this.filterGroups[0].filterRules);

        const mergedRules = this.filterService.intersectAndEnrich(oldRules, newRules);

        this.filterGroups[0].filterRules = mergedRules;

        // Replace with observables
        // $scope.$broadcast('setFilterGroupRules', {
        //     index: 0,
        //     filterRules: mergedRules
        // });

        this.checkForErrors(mergedRules);
    }

    checkForErrors(filters?: any[]) {
        // eslint-disable-next-line angular/definedundefined
        if (filters !== undefined && filters !== null) {
            let errorMessage = this.filterService.parseErrorMessageFromRules(filters);

            this.faultyFilterRules.firstLine = errorMessage;
            this.showBadFilterInfo = !!errorMessage;

            if (this.customErrorMessage) {
                errorMessage = this.filterService.parseErrorMessageFromRules(filters, this.customErrorMessage);
            }

            // Emit error message to parent
            this.setErrorMessage({
                message: errorMessage
            });
        }
    }

    private capitalizeFirstLetter(text: string): string {
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    private reloadFilterFields() {
        const options = {
            allPipelines: this.allPipelines,
            pipelineId: this.pipeline
        };
        this.filterService.loadFilterFields(this.entity, options).then((response: any) => {
            if (response) {
                this.filterFields = response.map((field: any) => {
                    return {
                        ...field,
                        label: this.filterFieldsTranslations[field.label] || field.label
                    };
                });
            }
        });
    }

    savedFiltersEnabled(): boolean {
        return this.flagsService.get('savedFilters') && !this.hideSavedFilters;
    }

    onSelectSavedFilter(savedFilterId: number): void {
        let savedFilter: any = null;

        if (savedFilterId !== 0) {
            savedFilter = this.savedFilters.find((filter: any) => filter.id === savedFilterId);
        }

        if (savedFilter?.id && this.appliedSavedFilter?.id && this.appliedSavedFilter.id === savedFilter.id) {
            return;
        }

        if (!savedFilter && !this.appliedSavedFilter) {
            return;
        }

        if (!this.isSavedFilterDirty) {
            this.appliedSavedFilter = savedFilter;
            return this.handleSelectedSavedFilter(savedFilter);
        }

        this.discardDialog.show(false, 'saved filter').then(() => {
            this.appliedSavedFilter = savedFilter;
            return this.handleSelectedSavedFilter(savedFilter);
        }).catch(() => {

            this.savedFilterMdSelectModel = this.appliedSavedFilter.id;
        });
    }

    private handleSelectedSavedFilter(savedFilter: any): any {
        this.filterService.setSavedFilterId(savedFilter?.id || null, this.entity);

        if (savedFilter === null) {
            // eslint-disable-next-line angular/timeout-service
            return setTimeout(() => {
                this.resetFilterRules();
                this.applyFilters();
            }, 0);
        }

        return this.savedFilterService.get(savedFilter.id, this.entity).then((response: any) => {
            const firstFilterGroup = {
                index: 0,
                filterRules: []
            };

            this.filters = response.data.filter_config.rules;
            if (this.fixedFilters) {
                this.filters = cloneDeep(this.mergeWithFilterFields(this.filters));
            }

            this.appliedSavedFilter.filter_config = response.data.filter_config;

            this.filterGroups = [firstFilterGroup];
            this.filterGroups[0].filterRules = this.filters;

            // Disable flag reset
            this.filters.forEach((rule: any) => {
                rule.disabled = false;
            });

            // eslint-disable-next-line angular/definedundefined
            if (this.filters !== undefined) {
                this.showBadFilterInfo = this.filters.some((rule: any) => rule.disabled);
            }

            this.applyFilters();
        });
    }

    private mergeWithFilterFields(filters: any) {
        return this.filterFields.map((filterField) => {

            const appliedFilter = filters.find((filter: any) => filter.id === filterField.id);
            if (appliedFilter) {
                return {
                    ...filterField,
                    ...appliedFilter
                };
            }

            return filterField;
        });
    }

    private stripEmptyRules(rules: any) {
        return rules.filter((rule: any) => {
            if (!rule.value && !rule.raw_value) {
                return false;
            }

            return true;
        });
    }

    saveFilter() {
        // Open Save Filter Dialog

        return this.dialogService.show({
            clickOutsideToClose: true,
            controllerAs: 'vm',
            escapeToClose: true,
            multiple: true,
            bindToController: true,
            templateUrl: 'app-ajs/components/savefilterdialog/savefilterdialog.html',
            controller: 'SaveFilterDialogController',
            onShowing: (scope: any, element: any) => {

                element.children('md-dialog').addClass('save-filter-dialog');
            },
            locals: {
                savedFilter: {
                    name: this.appliedSavedFilter?.name,
                    shared: this.appliedSavedFilter ? this.appliedSavedFilter.shared : true
                },
                showUpdateAndCreateChoice: !!this.appliedSavedFilter,
                entity: this.entity
            },
            fullscreen: !this.media.isActive('gt-sm')
        }).then((savedFilterConfig: any) => {
            if (!savedFilterConfig) {
                return;
            }

            // Build filter object
            const filter: any = {
                name: savedFilterConfig.name,
                shared: savedFilterConfig.shared,
                filter_config: {
                    condition: 'AND',
                    rules: this.getRules()
                }
            };

            // Prepare copy for HTTP (clean up rules)
            const filterForHttp = cloneDeep(filter);
            filterForHttp.filter_config.rules = this.filterService.cleanAdvancedFilterForHttp(filterForHttp.filter_config.rules, this.entity === 'lead');

            // Check if updating existing filter
            if (savedFilterConfig.saveAction === 'UPDATE_EXISTING') {
                return this.savedFilterService.update(this.appliedSavedFilter.id, this.entity, filterForHttp).then(() => {
                    Object.assign(this.appliedSavedFilter, filter);
                    this.isSavedFilterDirty = false;
                    return this.utilsService.showSuccessToast(this.translate.instant('Saved filter updated.'));
                });
            }
            else {
                // Creating new filter
                return this.savedFiltersService.create(this.entity, filterForHttp).then((response: any) => {
                    filter.id = response.data.id;
                    this.appliedSavedFilter = filter;
                    this.savedFilterMdSelectModel = filter.id;
                    this.savedFilters.push(filter);
                    this.filterService.setSavedFilterId(this.appliedSavedFilter.id, this.entity);
                    this.isSavedFilterDirty = this.isSavedFilterDirtyCheck();

                    this.utilsService.showSuccessToast(this.translate.instant('Created a new saved filter.'));
                });
            }
        });
    }

    manageSavedFilters() {

        return this.dialogService.show({
            clickOutsideToClose: true,
            controllerAs: 'vm',
            escapeToClose: true,
            multiple: true,
            templateUrl: 'app-ajs/components/managesavedfiltersdialog/managesavedfiltersdialog.html',
            controller: 'ManageSavedFiltersDialogController',
            locals: {
                savedFilters: this.savedFilters,
                entity: this.entity
            },
            bindToController: true,
            fullscreen: !this.media.isActive('gt-sm')
        }).then(() => {
            return this.getSavedFilters();
        });
    }

    toggleMenu() {
        // This.onClose();
        this.toggleRequested.emit();
    }

    addFilterGroup() {
        const newFilterGroup = {
            index: this.index,
            filterRules: []
        };
        this.index++;
        this.filterGroups.push(newFilterGroup);
    }

    getRemoveFilterGroupFn(groupIndex: number) {
        return () => this.removeFilterGroup(groupIndex);
    }

    removeFilterGroup(filterGroupIndex: number) {
        const groupIndex = this.filterGroups.findIndex((group) => group.index === filterGroupIndex);
        if (groupIndex !== -1) {
            this.filterGroups.splice(groupIndex, 1);
        }
    }

    boundUpdateFilterGroup = this.updateFilterGroup.bind(this);

    private updateFilterGroup(args: { groupIndex: number, updatedGroups: any[] }) {
        this.showBadFilterInfo = args.updatedGroups.some((rule) => rule.disabled);

        const groupIdx = this.filterGroups.findIndex((group) => group.index === args.groupIndex);
        if (groupIdx !== -1) {
            this.filterGroups[groupIdx].filterRules = cloneDeep(args.updatedGroups);
        }

        // Check if empty → reset to defaults
        if (this.filterGroups.length === 1 && this.filterGroups[0].filterRules.length === 0) {
            this.resetFilterGroups(true);
        }

        this.checkForErrors(args.updatedGroups);
    }

    boundApplyFilters = () => this.applyFilters();

    applyFilters() {
        const rules = this.getRules();

        this.onFilterApply({
            rules,
            saved_filter_id: this.appliedSavedFilter?.id
        });

        this.checkForErrors(rules);
        this.isSavedFilterDirty = this.isSavedFilterDirtyCheck();
    }

    resetFilterGroups(noDefaultFilters = false) {
        if (!noDefaultFilters && this.filterGroups.length > 0 && this.filterGroups[0].filterRules !== this.getDefaultFiltersWrapper()) {
            const confirm = this.dialogService.confirm({ multiple: true })
                .clickOutsideToClose(true)
                .escapeToClose(true)
                .title(this.translate.instant('Are you sure you want to reset this filter?'))
                .textContent(this.translate.instant('You will lose your current progress.'))
                .ok(this.translate.instant('I\'m sure'))
                .cancel(this.translate.instant('cancel'));

            return this.dialogService.show(confirm).then(() => {

                this.resetFilterRules(noDefaultFilters);
                this.checkForErrors(this.getRules());
                if (this.fixedFilters) {
                    this.filterFields.forEach((filterField) => {
                        delete filterField.value;
                    });
                }

                return this.applyFilters();
            });
        }
        else {
            this.resetFilterRules(noDefaultFilters);
            return this.applyFilters();
        }
    }

    private getDefaultFiltersWrapper() {
        const defaultFilters = this.getDefaultFilters();

        if (this.fixedFilters) {
            return cloneDeep(this.mergeWithFilterFields(defaultFilters));
        }

        return defaultFilters;
    }

    private resetFilterRules(noDefaultFilters = false) {
        this.filterGroups = [];
        this.index = 0;

        let filterRules = noDefaultFilters
            ? []
            : (this.appliedSavedFilter ? this.appliedSavedFilter.filter_config.rules : (this.getDefaultFiltersWrapper() || []));

        if (this.fixedFilters) {
            filterRules = cloneDeep(this.mergeWithFilterFields(filterRules));
        }

        filterRules = filterRules.map((rule: any) => ({ ...rule, disabled: false }));

        const firstGroup = {
            index: this.index,
            filterRules
        };

        this.index++;
        this.filterGroups.push(firstGroup);
    }

    getSavedFilterName(): string {
        const defaultText = this.translate.instant('Default');
        const ret = this.appliedSavedFilter ? (this.appliedSavedFilter.name + (this.isSavedFilterDirty ? '*' : '')) :
            (defaultText + (this.isSavedFilterDirty ? '*' : ''));

        return ret;
    }

    getNoSavedFiltersMessage(): string {
        return this.translate.instant('No {{entity}} filters have been saved yet. Build a filter below and save it so you can easily re-use it.', { entity: this.entity });
    }

    private getRules(): any[] | undefined {
        if (this.filterGroups[0].filterRules.length === 0) {
            return [];
        }

        // Remove empty/incomplete rules
        let rules = cloneDeep(this.filterGroups[0].filterRules.filter((rule: any) => rule.id || rule.operator));

        // Skip if not ready
        const notReady = rules.find((rule: any) => rule.disabled !== false && rule.disabled !== true);
        if (notReady) {
            return;
        }

        // Transform date inputs, remove unwanted fields
        rules = rules?.map((rule: any) => {
            delete rule.index;
            delete rule.disabled;

            if (rule.input === 'date' && rule.operator && ['after', 'on', 'before'].includes(rule.operator)) {
                rule.value = this.utilsService.formatDate(rule.value, 'yyyy-MM-dd');
            }

            return rule;
        });

        // Clean incomplete rules before sending them to the API
        rules = rules.filter((rule: any) => {

            if (!rule.id || !rule.operator
                || (!rule.value && rule.value !== 0)
                || rule.value.length === 0
                || rule.operator === 'between'
                || rule.operator === 'not_between') {

                if (rule.operator && (rule.operator === 'between' || rule.operator === 'not_between')) {
                    if (!isArray(rule.value) || rule.value.length !== 2
                        // eslint-disable-next-line angular/definedundefined
                        || (rule.value[0] === undefined || rule.value[0] === null) || (rule.value[1] === undefined || rule.value[1] === null)) {

                        return false;
                    }
                }
                else if (!!rule.operator
                    && (rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty'
                        && rule.operator !== 'is_null' && rule.operator !== 'is_not_null'
                        && rule.operator !== 'true' && rule.operator !== 'false')) {
                    return false;
                }
                else if (!rule.operator) {
                    return false;
                }
            }

            return true;
        });

        return rules;
    }

    private getSavedFilters() {
        this.savedFiltersService.get(this.entity).then((response: any) => {
            this.savedFilters = response.data;
            const appliedSavedFilterId = this.filterService.getSavedFilterId(this.entity);

            if (!appliedSavedFilterId) {
                this.isSavedFilterDirty = this.isSavedFilterDirtyCheck();
                return;
            }

            const foundFilter = this.savedFilters.find((filter: any) => filter.id === appliedSavedFilterId);
            if (foundFilter) {
                return this.savedFilterService.get(appliedSavedFilterId, this.entity).then((filterResponse: any) => {
                    this.savedFilterMdSelectModel = filterResponse.data.id;
                    this.appliedSavedFilter = filterResponse.data;
                    this.isSavedFilterDirty = this.isSavedFilterDirtyCheck();
                });
            }
        });
    }

    private isSavedFilterDirtyCheck(): boolean {
        if (this.hideSavedFilters) {
            return false;
        }

        if (!this.appliedSavedFilter) {
            return this.filterService[`is${this.capitalizeFirstLetter(this.entity)}FilterApplied`]();
        }

        if (this.fixedFilters) {
            return !this.filterService.isFilterEqual(
                this.appliedSavedFilter.filter_config.rules,
                this.stripEmptyRules(this.filterGroups[0]?.filterRules)
            );
        }

        return !this.filterService.isFilterEqual(
            this.appliedSavedFilter.filter_config.rules,
            this.filterGroups[0]?.filterRules
        );
    }

}
